import { Pipe, PipeTransform } from '@angular/core';
import { KeywordLocationModel } from '../../../Mentions/models/keyword-location.model';
import { KeywordHighlightModel } from '../../models/keyword-highlight.model';
import { ContexualKeywordIndicesResponseModel } from '../../../../api/mentions/model/contexual-keyword-indices-response.model';

/**
 * This pipe MUST be used with the IndexHighlightPipeSup otherwise it will not work
 * this pipe only add marking for highlight based on the positions returned by b/e
 */
@Pipe({
    name: 'indexHighlight',
    standalone: false
})
export class IndexHighlightPipe implements PipeTransform {
    transform(
        text: string,
        searches: KeywordLocationModel[],
        keywords: KeywordHighlightModel[]
    ): string {
        if (!text) {
            return '';
        }
        let result = text;

        if (!searches || !keywords) {
            return result;
        }

        // reverse the locaion so that the substitution does not affect the location start index
        const searchesReverse = this.mergeLocations(searches).reverse();

        // create a map for keyword and the highlight class
        const keyToClassMap: any = [];
        for (const highlightModel of keywords){
            for (const k of highlightModel.keywords){
                keyToClassMap[k.toLocaleLowerCase()] = highlightModel.class.replace(/--?/g, '');
            }
        }
        searchesReverse.forEach(search => {
            const sub = result.substring(search.characterStartPoint, search.characterEndPoint);
            if (sub) {
                const before = result.substring(0, search.characterStartPoint);
                const after = result.substring(search.characterEndPoint);
                const className = keyToClassMap[sub.toLowerCase()] ?? 'agprhighlight';
                let wrapper = `${className}start{{0}}${className}end`;
                wrapper = wrapper.replace('{{0}}', sub);
                result = before + wrapper + after;
            }
        });
        return result;
    }

    /**
     * We need to merge the location ranges based on their start and end value.
     *  i.e [1,3], [2,10] => [1,10]
     * @param locations the original locations
     * @returns a new array containing the merged locations
     */
    mergeLocations(locations: KeywordLocationModel[]): KeywordLocationModel[]{
        const sorted = locations.slice().sort((a, b) => a.characterStartPoint - b.characterStartPoint);
        let i = 1;
        while (i < sorted.length){
            const prev = sorted[i - 1];
            const current = sorted[i];

            if (prev.characterEndPoint >= current.characterStartPoint) {
                sorted.splice(i - 1, 2, new KeywordLocationModel(
                    {start: prev.characterStartPoint, end: Math.max(prev.characterEndPoint, current.characterEndPoint)}
                ));
            } else {
                i++;
            }
        }
        return sorted;
    }
}

/**
 * This pipe MUST be used with the IndexHighlightPipeSup otherwise it will not work
 * same as indexHighlight but searches are ContexualKeywordIndicesResponseModel, which comes from the BE
 * which is used in BMIE
 */
@Pipe({
    name: 'indexHighlightApi',
    standalone: false
})
export class IndexHighlightApiPipe implements PipeTransform {
    transform(
        text: string,
        searches: ContexualKeywordIndicesResponseModel[],
        keywords: KeywordHighlightModel[]
    ): string {
        if (!text) {
            return '';
        }
        let result = text;

        if (!searches || !keywords) {
            return result;
        }

        // reverse the locaion so that the substitution does not affect the location start index
        const searchesReverse = this.mergeLocations(searches).reverse();

        // create a map for keyword and the highlight class
        const keyToClassMap: any = [];
        for (const highlightModel of keywords){
            for (const k of highlightModel.keywords){
                keyToClassMap[k.toLocaleLowerCase()] = highlightModel.class.replace(/--?/g, '');
            }
        }
        searchesReverse.forEach(search => {
            const sub = result.substring(search.start, search.end);
            if (sub) {
                const before = result.substring(0, search.start);
                const after = result.substring(search.end);
                const className = keyToClassMap[sub.toLowerCase()] ?? 'agprhighlight';
                let wrapper = `${className}start{{0}}${className}end`;
                wrapper = wrapper.replace('{{0}}', sub);
                result = before + wrapper + after;
            }
        });
        return result;
    }

    /**
     * We need to merge the location ranges based on their start and end value.
     *  i.e [1,3], [2,10] => [1,10]
     * @param locations the original locations
     * @returns a new array containing the merged locations
     */
    mergeLocations(locations: ContexualKeywordIndicesResponseModel[]): ContexualKeywordIndicesResponseModel[]{
        const sorted = locations.slice().sort((a, b) => a.start - b.start);
        let i = 1;
        while (i < sorted.length){
            const prev = sorted[i - 1];
            const current = sorted[i];

            if (prev.end >= current.start) {
                sorted.splice(i - 1, 2, {
                    start: prev.start,
                    end: Math.max(prev.end, current.end)
                });
            } else {
                i++;
            }
        }
        return sorted;
    }
}
/**
 * Supplimentary pipe for the index highlight pipe
 * Substitue all the highlight marking to the real highlight html
 */
@Pipe({
    name: 'indexHighlightSup',
    standalone: false
})
export class IndexHighlightPipeSup implements PipeTransform{
    transform(text: string): string{
        if (!text) {
            return '';
        }
        let replace = text.replace(/agprhighlightstart/g, '<span class="agpr-highlight">');
        replace = replace.replace(/agprhighlightfilterstart/g, '<span class="agpr-highlight--filter">');
        replace = replace.replace(/agprhighlightsocialstart/g, '<span class="agpr-highlight--social">');
        replace = replace.replace(/agprhighlight(filter)?(social)?end/g, '</span>');
        return replace;
    }
}
